// The sidecar access map — client side.
//
// The API returns customer rows FLAT and adds one optional `_access` key
// describing per-field access. Nothing else about the response shape changes,
// which is the point: existing `customer.first_name` reads keep working, and a
// surface adopts `_access` when it converts rather than all at once.
// (Q13, context/backlog/authorization-system-rebuild.md.)
//
// Why this matters for edit modals specifically. Before the sidecar, a field
// the caller could not see arrived as `null`, `initialValue()` turned it into
// `''`, and it rendered as an EMPTY EDITABLE INPUT. The modal sends every
// field in the block on save — there is no dirty-field diff — so an operator
// could blank a field they were never allowed to see, with a `dx_reason`
// justifying something else entirely. `_access` closes that by folding into
// the modal's existing `readOnly` flag, which is already excluded from the
// patch body rather than merely disabled.

import type { z } from 'zod';
import type { MaskType } from '@commissions/auth';
import type { EditFieldSpec } from './edit-field-group-modal';

/**
 * Mask types are an EXTENSIBLE enum, declared once in @commissions/auth
 * (`policy/mask.ts`). `hidden` withholds the value; `last4` is a partial mask
 * the SERVER applies, so the value arrives already masked and renders as sent.
 * An older client meeting a newer type must degrade to hidden, never crash —
 * so treat this as an open set.
 */
export type { MaskType };

export interface FieldAccess {
  /** The value is withheld. `mask` says how it is presented. */
  masked?: boolean;
  /** Presentation for a masked value; unknown values degrade to `hidden`. */
  mask?: MaskType;
  /** Whether the caller may write this field. Absent means "no restriction". */
  editable?: boolean;
  /**
   * Why the server decided as it did. Only `rule` is read here: it separates
   * `system_managed` ("no human may write this") from `not_granted` ("this
   * caller lacks the grant") — see isEditDenied.
   */
  reason?: { rule?: string };
}

/** Keyed by field name, exactly as the row spells it. */
export type AccessMap = Record<string, FieldAccess>;

/**
 * What a masked value renders as. Never the empty string — see maskLabel.
 *
 * `####` rather than `•••` (Craig, 2026-08-18): it must be distinguishable at a
 * glance from the `—` an EMPTY field renders (section.tsx's Field), because
 * "withheld from you" and "nobody ever filled this in" are different facts and
 * an operator acts differently on each. Dots read as a styling flourish; hashes
 * read as redaction.
 */
const MASK_PLACEHOLDER = '####';

/**
 * Display text for a masked field.
 *
 * Deliberately NOT '' — a masked field that renders as blank is
 * indistinguishable from a genuinely empty one, which is the ambiguity the
 * whole masked-not-elided rule exists to remove.
 *
 * `section.tsx`'s `Field` renders it for a masked field, and so do the modals
 * that render their own controls.
 */
export function maskLabel(access: FieldAccess | undefined): string {
  // A partial mask's value arrives already masked and is rendered as sent
  // (`showsMaskedValue`), so only `hidden` reaches here on purpose. The default
  // is what makes an older client tolerate a newer server rather than crash on
  // a type it has never heard of.
  switch (access?.mask) {
    case 'hidden':
    default:
      return MASK_PLACEHOLDER;
  }
}

/** Is this field masked for the current caller? */
export function isMasked(access: AccessMap | undefined, field: string): boolean {
  return Boolean(access?.[field]?.masked);
}

/**
 * Does the row carry this masked field's value in its masked form — so the
 * surface renders the value as sent (`####6789`, or `—` when empty) instead
 * of the placeholder?
 */
export function showsMaskedValue(access: FieldAccess | undefined): boolean {
  return access?.masked === true && access.mask === 'last4';
}

/**
 * Fold `_access` into a field-spec list before the modal renders it.
 *
 * A field becomes `readOnly` when the caller may not edit it OR may not see
 * it — you cannot meaningfully edit a value you were not shown. `readOnly` is
 * the existing "display but never patch" mechanism (see EditFieldGroupModal's
 * onSubmit), so a denied field is excluded from the request body, not merely
 * disabled in the DOM.
 *
 * Absent `_access`, or an absent entry, means NO restriction — an
 * unconverted surface and an older server must both keep working. Denial is
 * expressed by the server saying so, not by silence. (The server side fails
 * closed; this is the client mirroring an explicit decision, not making one.)
 *
 * Config `readOnly` is never widened: it and access-driven readOnly are
 * independent reasons, and a permissive `_access` entry must not override the
 * surface's own decision that a field is display-only.
 */
export function applyAccess(
  fields: readonly EditFieldSpec[],
  access: AccessMap | undefined,
): EditFieldSpec[] {
  if (!access) return fields as EditFieldSpec[];

  return fields.map((field) => {
    const entry = access[field.name];
    if (!entry) return field;

    const denied = entry.editable === false || entry.masked === true;
    if (!denied) return field;
    // A value served in its masked form is displayed as sent, read-only.
    const masked = entry.masked === true && !showsMaskedValue(entry) ? { masked: true } : {};
    if (field.readOnly) return { ...field, ...masked };

    return { ...field, readOnly: true, ...masked };
  });
}

/**
 * May the current caller NOT write this field, for a reason that is about
 * THEM — the field is masked, or its edit unit is not granted?
 *
 * For the hand-built modals (override, adjustment, qualifier, coding), which
 * cannot use `applyAccess` because they render their own controls.
 *
 * ⚠️ Deliberately NOT `editable === false`. `buildAccess` also emits that for
 * `system_managed` fields — `partner_id`, `period_id`, `override_id`,
 * `coding_type_id`'s neighbours — which are the row's IDENTITY: set once on
 * Add, never edited. Those modals already lock them in edit mode themselves,
 * and treating them as denied would lock the Add form's key pickers for every
 * caller. So a `system_managed` refusal is left to the modal; a permission
 * refusal makes the field read-only.
 *
 * Absent map or absent entry → false (no restriction), matching applyAccess.
 */
export function isEditDenied(access: AccessMap | undefined, field: string): boolean {
  const entry = access?.[field];
  if (!entry) return false;
  if (entry.masked === true) return true;
  return entry.editable === false && entry.reason?.rule !== 'system_managed';
}

/**
 * The modal's zod schema minus every field the caller may not edit.
 *
 * A denied field is not a form control (it renders as ReadOnlyValue), so its
 * rules must not apply either: a MASKED required field arrives null, its
 * default is '', and `min(1)` would keep Save disabled forever with nothing on
 * screen to fix. Omitted, it is neither validated nor in the submitted values —
 * so it cannot reach a request by accident.
 */
export function withoutDenied<S extends z.ZodObject>(schema: S, access: AccessMap | undefined): S {
  const denied = Object.keys(schema.shape).filter((k) => isEditDenied(access, k));
  if (denied.length === 0) return schema;
  // Widened for the call: the mask is built at runtime, so no literal key type
  // exists to check it against. The form's own type stays the full schema.
  const mask: Record<string, true> = Object.fromEntries(denied.map((k) => [k, true]));
  return (schema as z.ZodObject).omit(mask) as unknown as S;
}
